/** Client-side gates for `POST /api/rooms` (curator ack + optional Turnstile). */

export const MIN_POLL_OPTIONS = 2;
export const MAX_POLL_OPTIONS = 15;

/** Matches Forge `launchUrls.js` SUMMARY_PARAM_MAX — Jira ticket title prefill cap. */
export const SEED_ISSUE_SUMMARY_MAX = 500;

export type LaunchSeedParams = {
	seedIssueKey: string;
	seedIssueSummary: string;
};

export function parseLaunchSeedParams(
	params: URLSearchParams | Readonly<Record<string, string | null | undefined>>,
): LaunchSeedParams {
	const read = (key: string): string => {
		const raw =
			params instanceof URLSearchParams
				? params.get(key)
				: (params[key] ?? null);
		return (raw ?? "").trim();
	};

	return {
		seedIssueKey: read("seedIssueKey"),
		seedIssueSummary: read("seedIssueSummary").slice(0, SEED_ISSUE_SUMMARY_MAX),
	};
}

/** Prefill poll question from Jira launch URL — never overwrites curator edits. */
export function prefillQuestionFromSeedSummary(
	currentQuestion: string,
	seedIssueSummary: string,
): string {
	const summary = seedIssueSummary.trim();
	if (!summary) return currentQuestion;
	if (currentQuestion.trim()) return currentQuestion;
	return summary.slice(0, SEED_ISSUE_SUMMARY_MAX);
}

export type CreateRoomFormFields = {
	question: string;
	options: string[];
	masterKeyAck: boolean;
	turnstileToken: string | null;
	turnstileRequired: boolean;
	creating: boolean;
};

export const CREATE_ROOM_CLIENT_MESSAGES = {
	curatorAckRequired:
		"Check the box confirming you’ll store the curator link safely, then try launching again.",
	turnstileRequired:
		"Complete the quick check below (separate from the curator acknowledgment), then try launching again.",
	turnstileMisconfigured:
		"This environment requires verification but the Turnstile widget isn’t available. Set TURNSTILE_SITE_KEY on the Worker (or VITE_TURNSTILE_SITE_KEY at client build) to match TURNSTILE_SECRET_KEY.",
} as const;

export function nonEmptyCreateOptions(opts: string[]): string[] {
	return opts.map((s) => s.trim()).filter(Boolean);
}

export function getCreateRoomBlockMessage(fields: CreateRoomFormFields): string | null {
	if (fields.creating) return null;

	const question = fields.question.trim();
	const filled = nonEmptyCreateOptions(fields.options);

	if (!question) return "Add a question before launching the poll.";
	if (filled.length < MIN_POLL_OPTIONS) return "Add at least two ideas before launching the poll.";
	if (filled.length > MAX_POLL_OPTIONS) return "Use at most fifteen ideas before launching the poll.";
	if (!fields.masterKeyAck) return CREATE_ROOM_CLIENT_MESSAGES.curatorAckRequired;

	if (fields.turnstileRequired && !fields.turnstileToken?.trim()) {
		return CREATE_ROOM_CLIENT_MESSAGES.turnstileRequired;
	}

	return null;
}

export function canSubmitCreateRoom(fields: CreateRoomFormFields): boolean {
	return getCreateRoomBlockMessage(fields) === null && !fields.creating;
}
